import { NgModule } from '@angular/core';
import { ExtraOptions, RouterModule, Routes } from '@angular/router';
import { ActivateAccountComponent } from '@authentication/activate-account/activate-account.component';
import { AuthenticationComponent } from '@authentication/authentication.component';
import { DemoComponent } from '@authentication/demo/demo.component';
import { LoginComponent } from '@authentication/login/login.component';
import { NewPasswordComponent } from '@authentication/new-password/new-password.component';
import { ResetPasswordComponent } from '@authentication/reset-password/reset-password.component';
import { SignUpComponent } from '@authentication/sign-up/sign-up.component';
import { accountInactiveGuard } from '@core/guards/account-inactive.guard';
import { adminRoleGuard } from '@core/guards/admin-role.guard';
import { authGuard } from '@core/guards/auth.guard';
import { freeOrCustomLicenseActiveGuard } from '@core/guards/free-or-custom-license-active.guard';
import { lecturerRoleGuard } from '@core/guards/lecturer-role.guard';
import { licenseNotSetOrSubscriptionGuard } from '@core/guards/license-not-set-or-subscription.guard';
import { licenseSetGuard } from '@core/guards/license-set.guard';
import { loginGuard } from '@core/guards/login.guard';
import { noStudentRoleGuard } from '@core/guards/no-student-role.guard';
import { nutritionRolesGuard } from '@core/guards/nutrition-roles.guard';
import { subscriptionLicenseActiveGuard } from '@core/guards/subscription-license-active.guard';
import { userActiveGuard } from '@core/guards/user-active.guard';
import { LayoutComponent } from '@core/layout/layout.component';
import { InactiveAccountInfoComponent } from '@shared/component/inactive-account-info/inactive-account-info.component';
import { MyAccountComponent } from '@shared/component/my-account/my-account.component';
import { RoutePath } from '@shared/route-path.enum';

export const routes: Routes = [
	{
		path: RoutePath.ADMIN,
		canActivate: [authGuard, userActiveGuard, licenseSetGuard, freeOrCustomLicenseActiveGuard, adminRoleGuard],
		loadChildren: () => import('./administration/administration.module').then((m) => m.AdministrationModule),
	},
	{
		path: RoutePath.NUTRITION,
		canActivate: [
			authGuard,
			userActiveGuard,
			licenseSetGuard,
			freeOrCustomLicenseActiveGuard,
			subscriptionLicenseActiveGuard,
			nutritionRolesGuard,
		],
		loadChildren: () => import('@nutrition/nutrition.module').then((m) => m.NutritionModule),
	},
	{
		path: RoutePath.UNIVERSITY,
		canActivate: [
			authGuard,
			userActiveGuard,
			licenseSetGuard,
			freeOrCustomLicenseActiveGuard,
			subscriptionLicenseActiveGuard,
			lecturerRoleGuard,
		],
		loadChildren: () => import('./university/university.module').then((m) => m.UniversityModule),
	},
	{
		path: RoutePath.LICENSE,
		canActivate: [authGuard, userActiveGuard, licenseNotSetOrSubscriptionGuard],
		loadChildren: () => import('./license/license.module').then((m) => m.LicenseModule),
	},
	{
		path: RoutePath.MY_ACCOUNT,
		canActivate: [authGuard, noStudentRoleGuard],
		component: LayoutComponent,
		children: [{ path: '', component: MyAccountComponent }],
	},
	{
		path: RoutePath.INACTIVE_ACCOUNT,
		canActivate: [authGuard, accountInactiveGuard],
		component: LayoutComponent,
		children: [{ path: '', component: InactiveAccountInfoComponent }],
	},
	{
		path: RoutePath.AUTH,
		canActivate: [loginGuard],
		component: AuthenticationComponent,
		children: [
			{
				path: RoutePath.LOGIN,
				component: LoginComponent,
			},
			{
				path: RoutePath.SIGN_UP,
				component: SignUpComponent,
			},
			{
				path: RoutePath.NEW_PASSWORD,
				component: NewPasswordComponent,
			},
			{
				path: RoutePath.SET_PASSWORD,
				component: NewPasswordComponent,
			},
			{
				path: RoutePath.RESET_PASSWORD,
				component: ResetPasswordComponent,
			},
			{
				path: RoutePath.ACCOUNT_ACTIVATED,
				component: ActivateAccountComponent,
			},
			{ path: '', redirectTo: RoutePath.LOGIN, pathMatch: 'full' },
		],
	},
	{
		path: RoutePath.DEMO,
		canActivate: [loginGuard],
		component: DemoComponent,
	},
	{ path: '**', redirectTo: RoutePath.NUTRITION },
];

const config: ExtraOptions = {
	useHash: false,
};

@NgModule({
	imports: [RouterModule.forRoot(routes, config)],
	exports: [RouterModule],
})
export class AppRoutingModule {}
